// APITemplateDocs.js
import { Box, Grid, GridItem, Text, VStack, HStack, Link, Divider, Heading, Code, Container, Button, Spinner } from "@chakra-ui/react";
import { ExternalLinkIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus'; // Adjust the import path based on your project structure
import React, { useState, useContext } from 'react';
import { AppContext } from '../components/AppContext'; // Adjust the import path based on your project structure
import { callApi } from '../callApi'; 
import { UnorderedList, ListItem } from "@chakra-ui/react";

function APITemplateDocs() {
    const { accessToken } = useContext(AppContext);
    const [version, setVersion] = useState([]);
    const [loadingVersion, setLoadingVersion] = useState(false);

  const scrollToSection = (event, sectionId) => {
    event.preventDefault();
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetVersionClick = async () => {
    setLoadingVersion(true);
    try {
      const response = await callApi('/version', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setVersion(data.version);
    } catch (error) {
      console.error("Error fetching version: ", error);
    }
    setLoadingVersion(false);
  };

  return (
    <Box textAlign="center" fontSize="xl">
        <Grid
        templateAreas={`"header header"    
                        "main results"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'350px 4fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
        fontWeight='bold'
        >
                {/* Header with menu */}
                    <HeaderMenus />

        {/* Navigation sidebar */}
        <GridItem area="main" bg='white' p={3}>
  <br/>
  
  <VStack textAlign="left" align="start" spacing={3}>
    <Link fontSize="lg" fontWeight="normal" target="_blank" href="/logviewer" 
      color="blue.500">
      View Logs
    </Link>

    {/* Header text saying Template Management API Documentation */}
    <Heading as="h3" size="m">Template Management API Documentation</Heading>
    <Divider />
    <Link fontSize="sm" fontWeight="normal" href="#overview">Overview</Link>
    <Link fontSize="sm" fontWeight="normal" href="#authentication">Authentication</Link>
    <Link fontSize="sm" fontWeight="normal" href="#data-models">Data Models</Link>
    <Link fontSize="sm" fontWeight="normal" href="#endpoints">Endpoints</Link>
    <VStack textAlign="left" align="start" spacing={1} pl={4}>
      <Link fontSize="sm" fontWeight="normal" href="#template-list">Get Template List</Link>
      <Link fontSize="sm" fontWeight="normal" href="#create-template">Create Template</Link>
      <Link fontSize="sm" fontWeight="normal" href="#case-types">Get Case Types</Link>
      
      <Link fontSize="sm" fontWeight="normal" href="/templatelist" isExternal>
        Try Template List <ExternalLinkIcon mx="2px" />
      </Link>
    </VStack>
    <Link fontSize="sm" fontWeight="normal" href="#error-codes">Error Codes</Link>
    <Link fontSize="sm" fontWeight="normal" href="#usage-examples">Usage Examples</Link>
    <Link fontSize="sm" fontWeight="normal" href="#business-logic">Business Logic</Link>
  </VStack>
  
  </GridItem>

        <GridItem area="results" bg='white' p={6} ml="30px" overflowY="auto" height="700px" width="1000px">
            <VStack spacing={8} textAlign="left" align="start">
            <Box id="overview">
                <Heading as="h2" size="lg" mb={2}>Overview</Heading>
                <Text fontSize="md">
                    The Template Management API provides endpoints for managing medical note templates. 
                    Templates are used to create standardized medical notes across different case types and specialties within healthcare organizations.
                </Text>
                <Text fontSize="md" mt={2}>
                    All templates are organization-scoped, ensuring data isolation between different healthcare organizations.
                </Text>
                </Box>
                <Divider />

                <Box id="authentication">
                    <Heading as="h2" size="lg" mb={2}>Authentication</Heading>
                    <Text fontSize="md">
                        All Template Management API endpoints require JWT Bearer token authentication. The same authentication system used for other Revedy APIs applies here.
                    </Text>
                    <Text fontSize="md" mt={4}>
                        The base URL for all Template API endpoints is <Code>https://api.revedy.io</Code>
                    </Text>
                    <Text fontSize="md" mt={2}>
                        Example authentication header:
                    </Text>
                    <Code p={2} bg="gray.100" children="Authorization: Bearer YOUR_ACCESS_TOKEN" />
                </Box>
                <Divider />

                <Box id="data-models">
                    <Heading as="h2" size="lg" mb={2}>Data Models</Heading>
                    <Text fontSize="md" mb={2}>
                        <strong>Template Model Structure:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`{
  "id": 1,                          // Primary key
  "name": "Cardiology Consultation", // Template display name
  "case_type": "cardiology",        // Medical specialty/case type
  "template_description": "Template for cardiac patient assessments including ECG interpretation and treatment planning",
  "is_active": true                 // Active status flag
}`} />
                    
                    <Text fontSize="md" mt={4}>
                        <strong>Key Fields:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem><Code>id</Code>: Unique identifier for the template</ListItem>
                        <ListItem><Code>name</Code>: Human-readable template name</ListItem>
                        <ListItem><Code>case_type</Code>: Medical specialty or case category</ListItem>
                        <ListItem><Code>template_description</Code>: Detailed description of template purpose and usage</ListItem>
                        <ListItem><Code>is_active</Code>: Whether the template is currently active</ListItem>
                    </UnorderedList>
                </Box>
                <Divider />

                <Box id="endpoints">
              <Heading as="h2" size="lg" mb={2}>Endpoints</Heading>
            </Box>

            <Box id="template-list">
                <Heading as="h3" size="md" mb={2}>Get Template List</Heading>
                <Text fontSize="md">
                    The <Code>/template_list</Code> endpoint retrieves a list of active templates with descriptions for selection purposes.
                </Text>
                <Text fontSize="md" mt={2}>
                    HTTP Method: <Code>GET</Code>
                </Text>
                <Text fontSize="md">
                    URL: <Code>https://api.revedy.io/template_list</Code>
                </Text>
                <Text fontSize="md">
                    Headers:
                </Text>
                <Code p={2} bg="gray.100" children={`Authorization: Bearer YOUR_ACCESS_TOKEN`} />
                <Text fontSize="md" mt={4}>
                    Example request:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`curl --request GET \\
--url 'https://api.revedy.io/template_list' \\
--header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`} />
                <Text fontSize="md" mt={4}>
                    Example response:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`[
  {
    "id": 1,
    "name": "Cardiology Consultation",
    "case_type": "cardiology",
    "template_description": "Template for cardiac patient assessments including ECG interpretation and treatment planning",
    "is_active": true
  },
  {
    "id": 2,
    "name": "Emergency Room Assessment",
    "case_type": "emergency",
    "template_description": "Template for ER patient assessments and triage procedures",
    "is_active": true
  }
]`} />
                <Text fontSize="md" mt={4}>
                    <strong>Features:</strong>
                </Text>
                <UnorderedList fontSize="md" mt={2} ml={6}>
                    <ListItem>Returns only active templates (is_active=true)</ListItem>
                    <ListItem>Filtered by user's organization for data isolation</ListItem>
                    <ListItem>Includes template descriptions for informed selection</ListItem>
                    <ListItem>Optimized for UI dropdowns and template selection interfaces</ListItem>
                </UnorderedList>
            </Box>
            <Divider />

            <Box id="create-template">
                <Heading as="h3" size="md" mb={2}>Create Template</Heading>
                <Text fontSize="md">
                    The <Code>/templates</Code> endpoint creates a new template from sample medical notes.
                </Text>
                <Text fontSize="md" mt={2}>
                    HTTP Method: <Code>POST</Code>
                </Text>
                <Text fontSize="md">
                    URL: <Code>https://api.revedy.io/templates</Code>
                </Text>
                <Text fontSize="md">
                    Headers:
                </Text>
                <Code p={2} bg="gray.100" children={`Authorization: Bearer YOUR_ACCESS_TOKEN`} />
                <Code p={2} bg="gray.100" children={`Content-Type: application/json`} />
                
                <Text fontSize="md" mt={4}>
                    <strong>Required Fields:</strong>
                </Text>
                <UnorderedList fontSize="md" mt={2} ml={6}>
                    <ListItem><Code>name</Code>: Template display name</ListItem>
                    <ListItem><Code>case_type</Code>: Medical case type/specialty</ListItem>
                    <ListItem><Code>template_description</Code>: Purpose and usage description</ListItem>
                    <ListItem><Code>sample_note</Code>: Sample medical note for AI training</ListItem>
                </UnorderedList>

                <Text fontSize="md" mt={4}>
                    Example request:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`curl --request POST \\
--url 'https://api.revedy.io/templates' \\
--header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
--header 'Content-Type: application/json' \\
--data '{
  "name": "Pediatric Well-Child Visit",
  "case_type": "pediatrics",
  "template_description": "Template for routine pediatric wellness examinations and developmental assessments",
  "sample_note": "Patient is a 5-year-old male presenting for routine well-child examination. Growth parameters: Height 42 inches (50th percentile), Weight 40 lbs (45th percentile). Developmental milestones appropriate for age. Immunizations up to date. Physical exam unremarkable."
}'`} />

                <Text fontSize="md" mt={4}>
                    <strong>AI Generation Process:</strong>
                </Text>
                <UnorderedList fontSize="md" mt={2} ml={6}>
                    <ListItem>Validates all required fields are present</ListItem>
                    <ListItem>Deactivates existing template with same case_type (if exists)</ListItem>
                    <ListItem>Uses advanced LLM service to generate optimized template from sample note</ListItem>
                    <ListItem>Creates new active template with generated content</ListItem>
                    <ListItem>Returns created template confirmation</ListItem>
                </UnorderedList>

                <Text fontSize="md" mt={4}>
                    Example response:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`{
  "id": 3,
  "name": "Pediatric Well-Child Visit",
  "case_type": "pediatrics",
  "is_active": true,
  "content": {
    "template_description": "Template for routine pediatric wellness examinations and developmental assessments"
  }
}`} />

                <Text fontSize="md" mt={4}>
                    Error Responses:
                </Text>
                <Code p={2} bg="gray.100" children={`400 Bad Request - Missing required fields`} />
                <Code p={2} bg="gray.100" children={`500 Internal Server Error - AI generation failure or database error`} />
            </Box>
            <Divider />

            <Box id="case-types">
                <Heading as="h3" size="md" mb={2}>Get Case Types</Heading>
                <Text fontSize="md">
                    The <Code>/case_types</Code> endpoint retrieves all distinct case types available in the organization's templates.
                </Text>
                <Text fontSize="md" mt={2}>
                    HTTP Method: <Code>GET</Code>
                </Text>
                <Text fontSize="md">
                    URL: <Code>https://api.revedy.io/case_types</Code>
                </Text>
                <Text fontSize="md">
                    Headers:
                </Text>
                <Code p={2} bg="gray.100" children={`Authorization: Bearer YOUR_ACCESS_TOKEN`} />
                <Text fontSize="md" mt={4}>
                    Example request:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`curl --request GET \\
--url 'https://api.revedy.io/case_types' \\
--header 'Authorization: Bearer YOUR_ACCESS_TOKEN'`} />
                <Text fontSize="md" mt={4}>
                    Example response:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`[
  {
    "id": "cardiology",
    "name": "cardiology"
  },
  {
    "id": "neurology",
    "name": "neurology"
  },
  {
    "id": "emergency",
    "name": "emergency"
  },
  {
    "id": "pediatrics",
    "name": "pediatrics"
  }
]`} />
                <Text fontSize="md" mt={4}>
                    <strong>Features:</strong>
                </Text>
                <UnorderedList fontSize="md" mt={2} ml={6}>
                    <ListItem>Returns distinct case types from active templates only</ListItem>
                    <ListItem>Scoped to user's organization</ListItem>
                    <ListItem>Used for template categorization and filtering</ListItem>
                    <ListItem>Useful for UI dropdowns when creating new templates</ListItem>
                </UnorderedList>
            </Box>
            <Divider />

            <Box id="error-codes">
                <Heading as="h2" size="lg" mb={2}>Error Codes</Heading>
                <Text fontSize="md">
                    The Template Management API uses the following HTTP status codes:
                </Text>
                <Container p={2} bg="gray.50" borderRadius="md" mt={2}>
                    <Text fontSize="sm"><Code children="200 OK" /> - Successful retrieval</Text>
                    <Text fontSize="sm"><Code children="201 Created" /> - Successful template creation</Text>
                    <Text fontSize="sm"><Code children="400 Bad Request" /> - Missing required fields or invalid data</Text>
                    <Text fontSize="sm"><Code children="401 Unauthorized" /> - Invalid or missing authentication token</Text>
                    <Text fontSize="sm"><Code children="500 Internal Server Error" /> - Database errors, AI service failures, or system errors</Text>
                </Container>
                
                <Text fontSize="md" mt={4}>
                    Common error response format:
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`{
  "error": "Missing required fields",
  "required": ["name", "case_type", "template_description", "sample_note"]
}`} />
            </Box>
            <Divider />

            <Box id="usage-examples">
                <Heading as="h2" size="lg" mb={2}>Usage Examples</Heading>
                
                <Text fontSize="md" mb={2}>
                    <strong>Loading Template Options for UI (JavaScript):</strong>
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`const loadTemplateOptions = async () => {
  const response = await fetch('/template_list', {
    headers: {
      'Authorization': \`Bearer \${authToken}\`
    }
  });
  
  const templates = await response.json();
  
  // Populate dropdown with descriptions
  const selectElement = document.getElementById('template-select');
  templates.forEach(template => {
    const option = document.createElement('option');
    option.value = template.id;
    option.textContent = \`\${template.name} - \${template.template_description}\`;
    option.setAttribute('data-case-type', template.case_type);
    selectElement.appendChild(option);
  });
};`} />

                <Text fontSize="md" mt={4} mb={2}>
                    <strong>Creating a New Template (JavaScript):</strong>
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`const createTemplate = async (templateData) => {
  const response = await fetch('/templates', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${authToken}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: 'Orthopedic Consultation',
      case_type: 'orthopedics',
      template_description: 'Template for orthopedic consultations including joint assessments and treatment planning',
      sample_note: 'Patient presents with right knee pain following sports injury. Physical examination reveals swelling and limited range of motion. X-rays show no fracture. Recommend MRI and physical therapy.'
    })
  });
  
  if (response.ok) {
    const newTemplate = await response.json();
    console.log('Template created:', newTemplate);
  } else {
    const error = await response.json();
    console.error('Error creating template:', error);
  }
};`} />

                <Text fontSize="md" mt={4} mb={2}>
                    <strong>Filtering Templates by Case Type:</strong>
                </Text>
                <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`const filterTemplatesByType = async (caseType) => {
  const response = await fetch('/template_list', {
    headers: {
      'Authorization': \`Bearer \${authToken}\`
    }
  });
  
  const templates = await response.json();
  
  // Filter by case type
  const filteredTemplates = templates.filter(template => 
    template.case_type === caseType
  );
  
  return filteredTemplates;
};`} />
            </Box>
            <Divider />


            <Box id="version">
                <Button onClick={handleGetVersionClick} isLoading={loadingVersion}>
                    Get Version
                </Button>
                {version && (
                    <>
                      <Text>Version: {version}</Text>
                    </>
                )}
            </Box>
            <Divider />

            </VStack>
            </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg='white' borderTop='1px' borderColor='gray.200' p={4}>
          {/* Footer content */}
        </GridItem>
      </Grid>
    </Box>
  );
}

export default APITemplateDocs;
